import React, { useState } from "react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import styled from "styled-components";
import classNames from "classnames";
import useOnClickOutside from "use-onclickoutside";
import Spinner from "./Spinner";

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `${r},${g},${b}`;
}

function attnShake(name, reps, magOdd, magEven) {
  const step = 100 / (reps + 1);
  const stepsOdd = [];
  const stepsEven = [];

  for (let i = 1; i <= reps; i += 1) {
    if (i % 2 === 1) {
      stepsOdd.push(i * step);
    } else {
      stepsEven.push(i * step);
    }
  }

  function stepsTemplate(steps, body) {
    if (steps.length === 0) return "";
    return `
      ${steps.map(s => `${s}%`).join(",\n")} {
        ${body}
      }
    `;
  }

  return `
    @keyframes ${name} {
      from,
      to {
        transform: translate3d(0, 0, 0);
      }
      ${stepsTemplate(stepsOdd, `transform: translate3d(${magOdd}, 0, 0);`)}
      ${stepsTemplate(stepsEven, `transform: translate3d(${magEven}, 0, 0);`)}
    }
  `;
}

const StyledSpinner = styled(Spinner)`
  --size: 2rem;
  width: var(--size);
  height: var(--size);
  vertical-align: middle;
  margin-left: 0.6rem;
  margin-top: -0.1rem;
  position: absolute;
`;

const DivStyles = styled.div`
  --borderColor: ${p => p.theme.colors.chineseWhite};
  --inputHeight: 3.6rem;
  --svgHeight: 1.8rem;
  --focusColor: #9eceff;
  --errorColor: red;
  --validatedColor: green;
  --errorBgColor: ${p => p.theme.colors.lavenderBlush};
  --duration: 0.4s;

  overflow: hidden;
  * {
    transition-property: all;
    transition-duration: var(--duration);
  }

  form {
    transition-property: all;
    transition-duration: var(--duration);
    position: relative;
    display: flex;
    flex-flow: column nowrap;
  }

  input,
  textarea {
    &::placeholder {
      color: ${p => p.theme.colors.darkGray};
    }
  }

  h2 {
    margin-top: 0;
  }

  .lead p {
    margin: 0rem;
  }

  p {
    font-size: 1.6rem;
  }

  ul {
    padding: 0;
    margin-top: 0;
    margin-bottom: 1.5rem;
  }

  textarea {
    border: 1px solid var(--borderColor);
    border-radius: 0.5rem;
    background-color: white;
    margin-bottom: 1.5rem;
    max-width: 100%;
    min-width: 100%;
    width: 100%;
    padding: 1rem;
    resize: none;

    &:focus {
      outline: none;
      border: 1px solid var(--focusColor);
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075),
        0 0 8px rgba(200, 220, 250, 0.6);
    }
  }

  button[type="submit"] {
    border: 1px solid var(--borderColor);
    background-color: white;
    border-radius: 0.4rem;
    padding: 1rem;
    position: relative;
    z-index: 1;
    ${StyledSpinner} {
      display: none;
      --bgRGB: ${p => hexToRgb("#ffffff")};
      --fgRGB: ${p => hexToRgb(p.theme.colors.darkGray)};
    }
    :not([disabled]):hover {
      ${StyledSpinner} {
        --bgRGB: ${p => hexToRgb(p.theme.colors.whisper)};
      }
      cursor: pointer;
      background-color: ${p => p.theme.colors.whisper};
      border-color: ${p => p.theme.colors.darkGray};
    }
  }

  form.submitting button[type="submit"] ${StyledSpinner} {
    display: initial;
  }

  .success-overlay {
    transition-property: all;
    transition-duration: var(--duration);
  }

  ${attnShake("shakeIcon", 3, "-5px", "5px")}

  li {
    input {
      height: var(--inputHeight);
      border: 1px solid var(--borderColor);
      display: inline-block;
      width: 100%;
      padding-left: 1rem;
      padding-right: 2.5rem;
      margin-right: -3rem;
      background-color: white;

      &:focus {
        border: 1px solid var(--focusColor);
        box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075),
          0 0 8px rgba(200, 220, 250, 0.6);
        outline: none;
      }
    }

    list-style: none;
    margin-top: -1px;
    &:first-child input {
      border-top-left-radius: 0.4rem;
      border-top-right-radius: 0.4rem;
    }
    &:last-child input {
      border-bottom-left-radius: 0.4rem;
      border-bottom-right-radius: 0.4rem;
    }
    svg {
      height: var(--svgHeight);
      vertical-align: top;
      margin-top: calc(0.5 * (var(--inputHeight) - var(--svgHeight)));
      fill: var(--borderColor);
    }

    &.focused {
      position: relative;
      svg {
        fill: var(--focusColor);
      }
    }

    &.validated {
      svg {
        fill: var(--validatedColor) !important;
        animation-name: flash;
        animation-duration: 0.5s;
      }
    }

    &.error {
      input {
        background-color: var(--errorBgColor);
      }
      svg {
        fill: var(--errorColor) !important;
        animation-name: shakeIcon;
        animation-duration: 0.3s;
      }
    }
  }

  textarea.error {
    background-color: var(--errorBgColor);
  }

  .group {
    position: relative;
  }

  .crossfade-enter {
    opacity: 0;
  }

  .crossfade-enter-active {
    opacity: 1;
  }

  .crossfade-exit {
    position: absolute;
    top: 0;
    width: 100%;
  }

  .crossfade-exit-active {
    opacity: 0;
  }

  .error-message {
    position: absolute;
    background-color: ${p => p.theme.colors.lavenderBlush};
    border: 1px solid ${p => p.theme.colors.firebrick};
    padding: 2rem;
    bottom: 5rem;
    animation-name: fadeInUp;
    animation-duration: 0.8s;
    border-radius: 0.4rem;
    p {
      margin: 0;
    }
  }

  .error-message-enter-active {
    animation-name: fadeInUp;
    animation-duration: 0.8s;
  }

  .error-message-exit-active {
    animation-name: fadeOutDown;
    animation-duration: 0.3s;
  }
`;

const svgEmail = (
  <svg
    aria-hidden="true"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
  >
    <path d="M14.998 3c0.001 0.001 0.001 0.001 0.002 0.002v9.996c-0.001 0.001-0.001 0.001-0.002 0.002h-13.996c-0.001-0.001-0.001-0.001-0.002-0.002v-9.996c0.001-0.001 0.001-0.001 0.002-0.002h13.996zM15 2h-14c-0.55 0-1 0.45-1 1v10c0 0.55 0.45 1 1 1h14c0.55 0 1-0.45 1-1v-10c0-0.55-0.45-1-1-1v0z" />
    <path d="M5.831 9.773l-3 2.182c-0.1 0.073-0.216 0.108-0.33 0.108-0.174 0-0.345-0.080-0.455-0.232-0.183-0.251-0.127-0.603 0.124-0.786l3-2.182c0.251-0.183 0.603-0.127 0.786 0.124s0.127 0.603-0.124 0.786z" />
    <path d="M13.955 11.831c-0.11 0.151-0.282 0.232-0.455 0.232-0.115 0-0.23-0.035-0.33-0.108l-3-2.182c-0.251-0.183-0.307-0.534-0.124-0.786s0.534-0.307 0.786-0.124l3 2.182c0.251 0.183 0.307 0.535 0.124 0.786z" />
    <path d="M13.831 4.955l-5.5 4c-0.099 0.072-0.215 0.108-0.331 0.108s-0.232-0.036-0.331-0.108l-5.5-4c-0.251-0.183-0.307-0.534-0.124-0.786s0.535-0.307 0.786-0.124l5.169 3.759 5.169-3.759c0.251-0.183 0.603-0.127 0.786 0.124s0.127 0.603-0.124 0.786v0z" />
  </svg>
);

const svgName = (
  <svg
    aria-hidden="true"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
  >
    <path d="M8 2c1.654 0 3 1.346 3 3s-1.346 3-3 3-3-1.346-3-3 1.346-3 3-3zM8 1c-2.209 0-4 1.791-4 4s1.791 4 4 4 4-1.791 4-4-1.791-4-4-4v0zM12 11c1.654 0 3 1.346 3 3h-14c0-1.654 1.346-3 3-3h8zM12 10h-8c-2.209 0-4 1.791-4 4v1h16v-1c0-2.209-1.791-4-4-4v0z" />
  </svg>
);

const svgPhone = (
  <svg
    aria-hidden="true"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
  >
    <path d="M12.614 16h-0.189c-1.026 0-2.569-0.48-4.234-1.316-1.627-0.817-3.112-1.838-4.075-2.801s-1.984-2.448-2.801-4.075c-0.836-1.665-1.316-3.208-1.316-4.234v-0.189l0.105-0.157c0.021-0.031 0.517-0.774 1.146-1.528 0.66-0.791 1.52-1.7 2.324-1.7 0.694 0 1.555 0.696 2.006 1.111 0.346 0.319 1.194 1.161 1.58 2.092 0.408 0.986 0.054 1.549-0.194 1.797-0.427 0.427-0.848 0.687-1.185 0.896-0.531 0.328-0.598 0.399-0.598 0.629 0 0.724 0.907 1.631 1.783 2.508s1.784 1.784 2.508 1.784c0.23 0 0.301-0.067 0.629-0.598 0.209-0.338 0.469-0.758 0.896-1.185 0.249-0.249 0.58-0.381 0.955-0.381 0.407 0 0.871 0.156 1.379 0.463 0.402 0.243 0.827 0.579 1.23 0.97 0.337 0.328 1.436 1.464 1.436 2.339 0 0.804-0.908 1.664-1.7 2.324-0.755 0.629-1.497 1.125-1.528 1.146l-0.157 0.105zM1.257 3.759c0.058 0.837 0.488 2.119 1.176 3.489 0.748 1.49 1.708 2.892 2.567 3.752s2.262 1.819 3.752 2.567c1.37 0.688 2.652 1.119 3.489 1.176 0.243-0.169 0.725-0.514 1.209-0.913 1.139-0.94 1.284-1.351 1.299-1.409-0.028-0.182-0.368-0.781-1.095-1.477-0.747-0.716-1.404-1.042-1.698-1.042-0.038 0-0.062 0.005-0.072 0.015-0.324 0.324-0.524 0.647-0.716 0.959-0.345 0.558-0.736 1.191-1.692 1.191-1.242 0-2.335-1.093-3.392-2.15s-2.15-2.15-2.15-3.392c0-0.956 0.633-1.347 1.191-1.692 0.312-0.193 0.635-0.392 0.959-0.716 0.019-0.019 0.033-0.134-0.046-0.355-0.165-0.461-0.631-1.093-1.215-1.648-0.632-0.602-1.107-0.839-1.244-0.862-0.059 0.016-0.471 0.162-1.415 1.306-0.398 0.483-0.739 0.96-0.907 1.201z" />
  </svg>
);

function encode(data) {
  return Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");
}

function validateName({ value, touched, lostFocusAfterEdit }) {
  return !(value.length > 0) && "Please provide a name";
}

function validatePhone({ value, touched, lostFocusAfterEdit }) {
  return (
    !(
      /^(\+{0,})(\d{0,})([(]{1}\d{1,3}[)]{0,}){0,}(\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}(\s){0,}$/gm.test(
        value
      ) && value.replace(/\D/g, "").length >= 7
    ) &&
    "Please provide a valid phone number and/or specify an email address below"
  );
}

function validateEmail({ value, touched, lostFocusAfterEdit }) {
  return (
    !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(value) &&
    "Please provide a valid email address and/or specify a phone number above"
  );
}

const [
  initial,
  validated,
  validationError,
  submitting,
  submitted,
  submissionError,
] = [
  {
    className: "initial",
    submitEnabled: true,
    inputsEnabled: true,
  },
  {
    className: "validated",
    submitEnabled: true,
    inputsEnabled: true,
  },
  {
    className: "validationError",
    submitEnabled: false,
    inputsEnabled: true,
  },
  {
    className: "submitting",
    submitEnabled: false,
    inputsEnabled: false,
  },
  {
    className: "submitted",
    submitEnabled: false,
    inputsEnabled: false,
  },
  {
    className: "submissionError",
    submitEnabled: true,
    inputsEnabled: true,
  },
];

export default function ContactForm({ className }) {
  const [state, setState] = useState({
    formStatus: initial,
    name: {
      name: "name",
      placeholder: "Name",
      value: "",
      focused: false,
      touched: false,
      lostFocusAfterEdit: false,
      errComponent: false,
      errResultant: false,
    },
    phone: {
      name: "phone",
      placeholder: "Phone",
      value: "",
      focused: false,
      touched: false,
      lostFocusAfterEdit: false,
      errComponent: false,
      errResultant: false,
    },
    email: {
      name: "email",
      placeholder: "Email",
      value: "",
      focused: false,
      touched: false,
      lostFocusAfterEdit: false,
      errComponent: false,
      errResultant: false,
    },
    message: {
      name: "message",
      placeholder: "Please type your message",
      value: "",
      focused: false,
      touched: false,
      lostFocusAfterEdit: false,
      errComponent: false,
      errResultant: false,
    },
  });

  function validateState(s) {
    const [nameError, phoneError, emailError] = [
      validateName(s.name),
      validatePhone(s.phone),
      validateEmail(s.email),
    ];

    s.name.errComponent = nameError;
    s.name.errResultant =
      s.name.touched && s.name.lostFocusAfterEdit && nameError;

    s.phone.errComponent = phoneError;
    s.phone.errResultant = false;

    s.email.errComponent = emailError;
    s.email.errResultant = false;

    s.message.errComponent = validateName(s.message);
    s.message.errResultant =
      s.message.touched &&
      s.message.lostFocusAfterEdit &&
      s.message.errComponent;

    if (phoneError && emailError) {
      s.phone.errResultant =
        s.phone.touched && s.phone.lostFocusAfterEdit && phoneError;
      s.email.errResultant =
        s.email.touched && s.email.lostFocusAfterEdit && emailError;
    } else if (!phoneError || !emailError) {
      // if one of them is valid, then other one is valid too if empty, otherwise existing validation rules apply
      s.phone.errResultant =
        s.phone.touched &&
        s.phone.lostFocusAfterEdit &&
        s.phone.value &&
        phoneError;
      s.email.errResultant =
        s.email.touched &&
        s.email.lostFocusAfterEdit &&
        s.email.value &&
        emailError;
    }

    s.formStatus = [
      s.name.errResultant,
      s.phone.errResultant,
      s.email.errResultant,
      s.message.errResultant,
    ].some(Boolean)
      ? validationError
      : validated;
    // console.log(s.formStatus, validated);
  }

  const handleChange = e => {
    const newState = {
      ...state,
    };
    newState[e.target.name].value = e.target.value;
    newState[e.target.name].touched = true;
    validateState(newState);
    setState(newState);
  };

  const handleFocus = e => {
    const newState = { ...state };
    newState[e.target.name].focused = true;
    setState(newState);
  };

  const handleBlur = e => {
    const newState = {
      ...state,
    };
    const o = newState[e.target.name];
    o.focused = false;
    // only count if lost focus after edit
    o.lostFocusAfterEdit = o.lostFocusAfterEdit || Boolean(o.value);
    validateState(newState);
    setState(newState);
  };

  const handleSubmit = e => {
    e.preventDefault();
    const newState = { ...state };
    [newState.name, newState.phone, newState.email, newState.message].forEach(
      function (p) {
        p.touched = true;
        p.lostFocusAfterEdit = true;
      }
    );
    validateState(newState);
    if (newState.formStatus !== validated) {
      setState(newState);
      return;
    }

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "contact",
        name: state.name.value,
        phone: state.phone.value,
        email: state.email.value,
        message: state.message.value,
      }),
    })
      .then(res =>
        setState({
          ...state,
          formStatus: res.status === 200 ? submitted : submissionError,
        })
      )
      .catch(error => setState({ ...state, formStatus: submissionError }));

    newState.formStatus = submitting;
    setState(newState);
  };

  const ta = state.message;
  const taClasses = {
    focused: ta.focused,
    error: ta.touched && ta.lostFocusAfterEdit && ta.errResultant,
    validated: ta.value && !ta.errComponent && ta.lostFocusAfterEdit,
  };

  const ref = React.useRef(null);
  const duration = 3000;

  const onEnter = e => {
    console.log("onEnter", e, ref);
    ref.current.style.height = `${e.scrollHeight}px`;
  };

  const onExit = e => {
    console.log("onExit", e);
  };

  const refFn = r => {
    console.log("refFn", r);
  };

  const errRef = React.useRef(null);
  useOnClickOutside(errRef, () =>
    setState({ ...state, formStatus: validated })
  );

  return (
    <DivStyles className={className}>
      <h2>Contact Us</h2>
      <div className="lead">
        <p>
          <span>Tel.:</span> 020 8814 3000
        </p>
        <p>
          <span>Fax:</span> 020 8572 5944
        </p>
        <p>
          <span>E-mail:</span> info@qcl-uk.com
        </p>
      </div>
      <div ref={ref}>
        <TransitionGroup className="group" appear>
          <CSSTransition
            key={state.formStatus !== submitted ? "form" : "success"}
            addEndListener={(node, done) => {
              node.addEventListener(
                "transitionend",
                e => {
                  if (e.target === node) {
                    ref.current.style.height = null;
                    done();
                  }
                },
                false
              );
            }}
            classNames="crossfade"
            onEntering={onEnter}
            onExit={onExit}
          >
            {state.formStatus !== submitted ? (
              <form
                name="contact"
                autoComplete="off"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                {...state.formStatus}
              >
                <input type="hidden" name="form-name" value="contact" />
                <ul>
                  {[
                    [state.name, svgName],
                    [state.phone, svgPhone],
                    [state.email, svgEmail],
                  ].map(([o, svg]) => {
                    const liClasses = {
                      focused: o.focused,
                      error: !o.focused && o.errResultant,
                      validated:
                        o.value && !o.errComponent && o.lostFocusAfterEdit,
                      // error: o.touched && o.lostFocusAfterEdit && o.errResultant,
                      // validated: o.value && !o.errComponent && o.lostFocusAfterEdit,
                    };
                    return (
                      <li className={classNames(liClasses)}>
                        <input
                          name={o.name}
                          placeholder={o.placeholder}
                          type="text"
                          autoComplete="off"
                          value={o.value}
                          maxLength="255"
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={!state.formStatus.inputsEnabled}
                        />
                        {svg}
                      </li>
                    );
                  })}
                </ul>

                <textarea
                  className={classNames(taClasses)}
                  placeholder={ta.placeholder}
                  name="message"
                  spellCheck="true"
                  rows="10"
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={!state.formStatus.inputsEnabled}
                >
                  {state.message.value}
                </textarea>
                <button
                  name="submit"
                  type="submit"
                  value="Submit"
                  onClick={handleSubmit}
                  disabled={!state.formStatus.submitEnabled}
                >
                  Submit
                  <StyledSpinner />
                </button>
                <CSSTransition
                  in={state.formStatus === submissionError}
                  mountOnEnter
                  unmountOnExit
                  appear
                  classNames="error-message"
                  addEndListener={(node, done) => {
                    node.addEventListener("animationend", done, false);
                  }}
                >
                  <div
                    ref={errRef}
                    className="error-message"
                    onClick={() =>
                      setState({ ...state, formStatus: validated })
                    }
                  >
                    <p>
                      Your contact request could not be submitted at this time.
                      Please try again later or contact us on the telephone
                      number listed above.
                    </p>
                  </div>
                </CSSTransition>
              </form>
            ) : (
              <div className="success-overlay">
                <h2
                  style={{
                    marginTop: "2rem",
                    marginBottom: "2rem",
                    color: "rgb(32, 145, 0)",
                  }}
                >
                  Thank you for contacting us
                </h2>
                <p>
                  We have received your message and will get in touch with you
                  as soon as possible.
                </p>
                <p>
                  If your enquiry is urgent, please use the telephone number
                  listed above to talk to one of our staff members.
                </p>
              </div>
            )}
          </CSSTransition>
        </TransitionGroup>
      </div>
    </DivStyles>
  );
}
